import { api } from "../lib/api";

const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "CODE", "PRE", "TEXTAREA", "INPUT", "SVG", "PATH"]);
const HAS_LETTER = /[A-Za-z]/;
const CACHE_VERSION = "2"; // bump to invalidate all cached translations in every browser

const memCache = {}; // { lang: { src: out } }

(function purgeStaleCache() {
  try {
    if (localStorage.getItem("hh_tr_ver") !== CACHE_VERSION) {
      Object.keys(localStorage)
        .filter((k) => k.startsWith("hh_tr_"))
        .forEach((k) => localStorage.removeItem(k));
      localStorage.setItem("hh_tr_ver", CACHE_VERSION);
    }
  } catch { /* ignore */ }
})();

function loadCache(lang) {
  if (memCache[lang]) return memCache[lang];
  try {
    memCache[lang] = JSON.parse(localStorage.getItem(`hh_tr_${lang}`) || "{}");
  } catch {
    memCache[lang] = {};
  }
  return memCache[lang];
}

function saveCache(lang) {
  try {
    localStorage.setItem(`hh_tr_${lang}`, JSON.stringify(memCache[lang]));
  } catch { /* ignore quota */ }
}

function collectNodes(root) {
  const nodes = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const parent = node.parentElement;
      if (!parent) return NodeFilter.FILTER_REJECT;
      if (parent.closest("[data-no-translate]")) return NodeFilter.FILTER_REJECT;
      let el = parent;
      while (el) {
        if (SKIP_TAGS.has(el.tagName)) return NodeFilter.FILTER_REJECT;
        el = el.parentElement;
      }
      const text = node.nodeValue;
      if (!text || !text.trim() || !HAS_LETTER.test(text)) return NodeFilter.FILTER_REJECT;
      if (node.__hh_out && node.nodeValue === node.__hh_out) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  let n;
  while ((n = walker.nextNode())) nodes.push(n);
  return nodes;
}

// Walk the current DOM and apply any cached translations. Fast, no network.
// Robust to React re-renders that recreate English text nodes.
function applyFromCache(lang) {
  const cache = loadCache(lang);
  const nodes = collectNodes(document.body);
  for (const node of nodes) {
    const src = node.nodeValue.trim();
    node.__hh_src = src;
    const out = cache[src];
    if (out != null) applyNode(node, out);
  }
}

// Find untranslated strings currently in the DOM, fetch them, cache, re-apply.
async function translateVisible(lang) {
  if (lang === "en") return;
  const cache = loadCache(lang);

  // 1) Apply whatever we already have first (instant).
  applyFromCache(lang);

  // 2) Collect strings still missing from cache.
  const nodes = collectNodes(document.body);
  const pending = [];
  const seen = new Set();
  for (const node of nodes) {
    const src = node.nodeValue.trim();
    if (cache[src] == null && !seen.has(src)) {
      seen.add(src);
      pending.push(src);
    }
  }
  if (!pending.length) return;

  try {
    const { data } = await api.post("/translate", { texts: pending, target: lang });
    const map = data.translations || {};
    let changed = false;
    for (const [src, out] of Object.entries(map)) {
      if (out && out.trim() !== src.trim()) {
        cache[src] = out; // never cache English fallbacks
        changed = true;
      }
    }
    if (changed) saveCache(lang);
    // 3) Re-apply to the CURRENT DOM (nodes may have been recreated meanwhile).
    applyFromCache(lang);
  } catch (e) {
    /* leave English on failure */
  }
}

function applyNode(node, out) {
  if (!out) return;
  const raw = node.nodeValue;
  const lead = raw.match(/^\s*/)[0];
  const trail = raw.match(/\s*$/)[0];
  const next = lead + out + trail;
  if (node.nodeValue === next) return;
  node.__hh_out = next;
  node.nodeValue = next;
}

let observer = null;
let debounceId = null;
let currentLang = "en";

function schedule() {
  clearTimeout(debounceId);
  debounceId = setTimeout(() => translateVisible(currentLang), 200);
}

export function startAutoTranslate(lang) {
  currentLang = lang;
  if (observer) observer.disconnect();

  if (lang === "en") return;

  translateVisible(lang);

  observer = new MutationObserver((mutations) => {
    let relevant = false;
    for (const m of mutations) {
      if (m.type === "childList" && m.addedNodes.length) { relevant = true; break; }
      if (m.type === "characterData") {
        const node = m.target;
        if (node.__hh_out && node.nodeValue === node.__hh_out) continue;
        relevant = true; break;
      }
    }
    if (!relevant) return;
    // Instantly re-apply known translations (handles re-renders), then fetch new ones.
    applyFromCache(currentLang);
    schedule();
  });
  observer.observe(document.body, { childList: true, subtree: true, characterData: true });
}

export function stopAutoTranslate() {
  if (observer) observer.disconnect();
  observer = null;
}
